/**
 * Horizon — a view-side cut-off over the tree (design spec §4 "Horizon
 * slider", §11.6). Never stored on nodes; lives in the Store like the hidden
 * tags and is persisted per document in local doc state.
 *
 *   { past: PRESET|ISO|null, ahead: PRESET|ISO|null,
 *     inExecutionOnly: boolean }
 *
 * - past  : show only nodes touched (created / edited / tagged / worked / done)
 *           within the last N days.
 * - ahead : show only nodes whose planned span or due date falls within the
 *           next N days.
 *   When both are set a node passes if EITHER holds ("recent or upcoming").
 * - inExecutionOnly : only nodes on the current period's Today list.
 *   This ANDs with the time window. Tag visibility handles tag filtering.
 *
 * A node hidden by the horizon is treated exactly like a node hidden by a
 * tag: it disappears unless a descendant is visible, so paths stay reachable.
 */

import { t } from "../../shared/i18n/index.js";

export const HORIZON_VIEWS = new Set(["bubble", "mindmap", "gantt", "kanban"]);
// `unit` and `count` name the period; `label` ("3 days") is read at render time.
const preset = (key, unit, count, extra) => ({
  key, unit, count, ...extra,
  get label() { return t(`views.horizon.${unit}`, { count }); },
});
export const HORIZON_PRESETS = [
  ...Array.from({ length: 7 }, (_, i) => preset(`${i + 1}d`, "day", i + 1, { days: i + 1 })),
  ...Array.from({ length: 4 }, (_, i) => preset(`${i + 1}w`, "week", i + 1, { days: (i + 1) * 7 })),
  ...Array.from({ length: 12 }, (_, i) => preset(`${i + 1}m`, "month", i + 1, { months: i + 1, days: (i + 1) * 30 })),
];

export const EMPTY_HORIZON = Object.freeze({ past: null, ahead: null, inExecutionOnly: false });

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function normalizeHorizon(raw) {
  const h = { ...EMPTY_HORIZON, ...(raw && typeof raw === "object" ? raw : {}) };
  // Preserve the old one-year preset when opening saved view preferences.
  if (h.past === "1y") h.past = "12m";
  if (h.ahead === "1y") h.ahead = "12m";
  const ok = (v) => v === null || v === undefined || HORIZON_PRESETS.some((p) => p.key === v) || ISO_DATE.test(String(v));
  // Drop the retired readyOnly preference so it cannot remain a hidden filter.
  return {
    past: ok(h.past) ? h.past || null : null,
    ahead: ok(h.ahead) ? h.ahead || null : null,
    inExecutionOnly: !!h.inExecutionOnly,
  };
}

export function isHorizonActive(h) {
  const n = normalizeHorizon(h);
  return !!(n.past || n.ahead || n.inExecutionOnly);
}

/** Resolve a preset or ISO date into a cut-off Date (start of day). */
export function horizonCutoff(value, today, direction) {
  if (!value) return null;
  const preset = HORIZON_PRESETS.find((p) => p.key === value);
  const base = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (preset?.months) {
    const day = base.getDate();
    base.setDate(1);
    base.setMonth(base.getMonth() + direction * preset.months);
    const lastDay = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
    base.setDate(Math.min(day, lastDay));
    return base;
  }
  if (preset) {
    base.setDate(base.getDate() + direction * preset.days);
    return base;
  }
  const d = new Date(`${value}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Latest timestamp (ms) a node was touched, from the data we have; null if none. */
export function lastTouchMs(node, lastWorkedDay = null) {
  let best = null;
  const consider = (v) => {
    if (v === null || v === undefined || v === "") return;
    const t = typeof v === "number" ? v : new Date(v).getTime();
    if (!Number.isNaN(t) && (best === null || t > best)) best = t;
  };
  consider(node._lastEditedAt);
  for (const t of node.tags || []) if (t && typeof t === "object") consider(t.setAt);
  if (lastWorkedDay) consider(`${lastWorkedDay}T12:00:00`);
  return best;
}

/**
 * Build the predicate `isOutOfHorizon(node)`; returns null when no horizon is set
 * so callers can skip the check entirely.
 * @param {object} horizon
 * @param {object} ctx
 * @param {Date} [ctx.today]
 * @param {Set<string>} [ctx.inExecution]  node ids on the current Today list
 * @param {(nodeId) => string|null} [ctx.lastWorkedDay]  from execution stats
 */
export function buildHorizonPredicate(horizon, ctx = {}) {
  const h = normalizeHorizon(horizon);
  if (!isHorizonActive(h)) return null;
  const today = ctx.today || new Date();
  const pastCut = horizonCutoff(h.past, today, -1);
  const aheadCut = horizonCutoff(h.ahead, today, +1);
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const inExecution = ctx.inExecution || new Set();
  const lastWorkedDay = ctx.lastWorkedDay || (() => null);

  const inTimeWindow = (node) => {
    if (!pastCut && !aheadCut) return true;
    if (pastCut) {
      const t = lastTouchMs(node, lastWorkedDay(node.id));
      if (t !== null && t >= pastCut.getTime()) return true;
    }
    if (aheadCut) {
      const nextDay=ctx.nextRoutineDay?.(node.id);
      if(nextDay){const next=new Date(`${nextDay}T00:00:00`);if(next<=aheadCut)return true;}
      const end = node.endDate ? new Date(`${node.endDate}T00:00:00`) : null;
      const start = node.startDate ? new Date(`${node.startDate}T00:00:00`) : null;
      // Due within the window, or a planned span that overlaps [today, ahead].
      if (end && !Number.isNaN(end.getTime()) && end <= aheadCut && end >= todayStart) return true;
      if (start && end && start <= aheadCut && end >= todayStart) return true;
    }
    return false;
  };

  return (node) => {
    if (!node) return false;
    if (h.inExecutionOnly && !inExecution.has(node.id)) return true;
    if (!inTimeWindow(node)) return true;
    return false;
  };
}
